import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Instagram, MessageCircle, Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { personalInfo } from '../../mock';

const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative py-20 px-6 border-t border-[#7C3AED]/20 overflow-hidden">
      {/* Background Radial Glow */}
      <div className="absolute inset-0 bg-gradient-radial from-[#7C3AED]/5 via-transparent to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          
          {/* Brand Block */}
          <div className="lg:col-span-1">
            <motion.h3
              className="text-3xl font-bold mb-4 bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#FF5C39] bg-clip-text text-transparent"
              style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
              whileHover={{ scale: 1.05 }}
            >
              MK
            </motion.h3>
            <p className="text-[#9D97B5] text-sm leading-relaxed mb-6">
              Digital marketer blending SEO, paid social, and content into systems that convert attention into revenue.
            </p>
            <div className="flex gap-4">
              <motion.a
                href="https://www.linkedin.com/in/maheen-kehkasha/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1A1430] border border-[#7C3AED]/20 flex items-center justify-center text-[#9D97B5] hover:text-[#7C3AED] hover:border-[#7C3AED] hover:bg-[#7C3AED]/10 transition-all duration-300"
                aria-label="LinkedIn"
                whileHover={{ scale: 1.15, rotate: 5 }}
              >
                <Linkedin size={18} />
              </motion.a>
              <motion.a
                href="https://www.instagram.com/websbymahi/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1A1430] border border-[#7C3AED]/20 flex items-center justify-center text-[#9D97B5] hover:text-[#C026D3] hover:border-[#C026D3] hover:bg-[#C026D3]/10 transition-all duration-300"
                aria-label="Instagram"
                whileHover={{ scale: 1.15, rotate: -5 }}
              >
                <Instagram size={18} />
              </motion.a>
              <motion.a
                href={personalInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1A1430] border border-[#7C3AED]/20 flex items-center justify-center text-[#9D97B5] hover:text-[#22D3A5] hover:border-[#22D3A5] hover:bg-[#22D3A5]/10 transition-all duration-300"
                aria-label="WhatsApp"
                whileHover={{ scale: 1.15, rotate: 5 }}
              >
                <MessageCircle size={18} />
              </motion.a>
            </div>
          </div>

          {/* Explore Column */}
          <div>
            <h4 className="text-[#EDE9FE] font-bold text-lg mb-6" style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}>
              Explore
            </h4>
            <ul className="space-y-3">
              {['About', 'Experience', 'Services', 'Projects', 'Skills', 'Testimonials'].map((item) => (
                <li key={item}>
                  <button
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="text-[#9D97B5] hover:text-[#FF5C39] transition-colors duration-300 text-sm group flex items-center gap-2"
                  >
                    <span className="w-0 group-hover:w-4 h-0.5 bg-[#FF5C39] transition-all duration-300" />
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Get In Touch Column */}
          <div>
            <h4 className="text-[#EDE9FE] font-bold text-lg mb-6" style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}>
              Get In Touch
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="mailto:connectmaheenk@gmail.com"
                  className="text-[#9D97B5] hover:text-[#7C3AED] transition-colors duration-300 text-sm flex items-start gap-3 group"
                >
                  <Mail size={18} className="mt-0.5 flex-shrink-0 group-hover:text-[#FF5C39]" />
                  <span>connectmaheenk@gmail.com</span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+917899275871"
                  className="text-[#9D97B5] hover:text-[#7C3AED] transition-colors duration-300 text-sm flex items-start gap-3 group"
                >
                  <Phone size={18} className="mt-0.5 flex-shrink-0 group-hover:text-[#FF5C39]" />
                  <span>+91 78992 75871</span>
                </a>
              </li>
              <li className="text-[#9D97B5] text-sm flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 flex-shrink-0 text-[#22D3A5]" />
                <span>Bengaluru, India</span>
              </li>
            </ul>
          </div>

          {/* CTA Column */}
          <div>
            <h4 className="text-[#EDE9FE] font-bold text-lg mb-6" style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}>
              Ready to Collaborate?
            </h4>
            <p className="text-[#9D97B5] text-sm leading-relaxed mb-6">
              Let&rsquo;s transform your digital presence into measurable growth.
            </p>
            <motion.button
              onClick={() => scrollToSection('contact')}
              className="px-6 py-3 bg-gradient-to-r from-[#7C3AED] to-[#C026D3] text-white rounded-full font-semibold text-sm hover:shadow-2xl hover:shadow-[#7C3AED]/50 transition-all duration-300 flex items-center gap-2 group relative overflow-hidden"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="relative z-10">Start a Project</span>
              <ExternalLink size={16} className="relative z-10 group-hover:translate-x-1 transition-transform" />
              {/* Radial Glow on Hover */}
              <span className="absolute inset-0 bg-gradient-radial from-[#FF5C39]/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-[#7C3AED]/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#9D97B5] text-sm">
            © 2025 <span className="text-[#EDE9FE] font-semibold">Maheen Kehkasha</span>. All rights reserved.
          </p>
          <p className="text-[#9D97B5] text-sm">
            Designed & built by <span className="text-[#EDE9FE] font-semibold">Maheen Kehkasha</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
